import { Component, inject, input, OnInit, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  AutoAttendantHoliday,
  CallFlow,
  VoiceAppViewEntity,
  WeeklySchedule,
} from '../../../../shared/models/generated/ziroModels';
import { NgClass } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { CallFlowGreetingsOptions } from '../call-flow-greetings/call-flow-greetings-form.component';
import { ButtonSizes, ButtonStyles, ButtonTypes, ZiroButtonComponent } from '../../../../button/button.component';
import { PipesModule } from '../../../../shared/pipes/pipes.module';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { TrimWithEllipsisFilter } from '../../../../shared/filters/trim-with-ellipsis.filter';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';

export interface UiCallFlow extends CallFlow {
  id: string;
  isHoliday?: boolean;
  isAfterHoursCallFlow?: boolean;
  holiday?: AutoAttendantHoliday;
  schedule?: WeeklySchedule;
  selectedGreetingOption?: CallFlowGreetingsOptions;
  selectedDestinationGreetingOption?: CallFlowGreetingsOptions;
  redirectCallSettingsEntity?: VoiceAppViewEntity;
}

@Component({
  selector: 'ziro-auto-attendant-call-flows-side-nav',
  templateUrl: './call-flows-side-nav.component.html',
  styleUrl: './call-flows-side-nav.component.scss',
  imports: [TranslateModule, NgClass, ZiroButtonComponent, PipesModule, NgbTooltip, ZiroFormsModule],
})
export class CallFlowsSideNavComponent implements OnInit {
  private _trimWithEllipsisFilter = inject(TrimWithEllipsisFilter);

  standardCallFlows = input.required<UiCallFlow[]>();
  holidayCallFlows = input<UiCallFlow[]>();
  afterHoursCallFlows = input<UiCallFlow[]>();
  flowsWithErrors = input<Set<string>>(new Set());
  selectedFlowId = input<string | null>(null);

  callFlowSelected = output<UiCallFlow>();
  duplicateHolidayCallFlow = output<UiCallFlow>();
  deleteHolidayCallFlow = output<UiCallFlow>();
  addHolidayCallFlow = output<void>();

  callFlowNameLimit = 40;

  ngOnInit(): void {
    const flows = this.standardCallFlows();
    if (flows.length > 0 && !this.selectedFlowId()) {
      this.selectFlow(flows[0]);
    }
  }

  hasError(flowId: string): boolean {
    return this.flowsWithErrors().has(flowId);
  }

  selectFlow(flow: UiCallFlow): void {
    this.callFlowSelected.emit(flow);
  }

  onDuplicate(flow: UiCallFlow, event: Event): void {
    event.stopPropagation();
    this.duplicateHolidayCallFlow.emit(flow);
  }

  onDelete(flow: UiCallFlow, event: Event): void {
    event.stopPropagation();
    this.deleteHolidayCallFlow.emit(flow);
  }

  onAddHoliday(): void {
    this.addHolidayCallFlow.emit();
  }

  tooltipOnTruncate(label: string): string {
    if (!label) {
      return '';
    }
    const trimmed = this._trimWithEllipsisFilter.transform(label, this.callFlowNameLimit);
    return trimmed.endsWith('...') ? label : '';
  }

  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonStyles = ButtonStyles;
  protected readonly ButtonSizes = ButtonSizes;
  protected readonly ButtonTypes = ButtonTypes;
}
